import { useRef, useState, useEffect } from "react";

type AudioMap = Record<string, string>;

// ──────────────────────────────────────────────────────────────────────
// CONFIG — flip USE_STREAMING to true once you've wired up MediaSource
// ──────────────────────────────────────────────────────────────────────
const USE_STREAMING = false;          // false = /speak, true = /speak_stream_new
const CONCURRENCY = 3;                // max parallel /speak requests
const TARGET_WORDS_PER_CHUNK = 30;    // sentence-aware target

export const useTTSQueue = () => {
  const [isMuted, setIsMuted] = useState(false);
  const audioMapRef = useRef<AudioMap>({});
  const [queue, setQueue] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioInstancesRef = useRef<HTMLAudioElement[]>([]);

  // Generation token: bumped on every stop. Async loops holding an older
  // generation must bail before doing more work.
  const generationRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Apply current mute state to every Audio element we've ever created
    // that's still in our tracking list. This covers:
    //   - The currently playing chunk
    //   - Any preloaded future chunks (Audio elements created early)
    //   - The streaming MediaSource audio element (if streaming is active)
    audioInstancesRef.current.forEach((a) => {
      try {
        a.muted = isMuted;
      } catch {}
    });

    // Also apply to audioRef as a belt-and-suspenders measure
    // (in normal cases it's already in audioInstancesRef, but no harm)
    if (audioRef.current) {
      try {
        audioRef.current.muted = isMuted;
      } catch {}
    }
  }, [isMuted]);

  // ──────────────────────────────────────────────────────────────────
  // SENTENCE-AWARE SPLIT
  // ──────────────────────────────────────────────────────────────────
  const splitText = (text: string): string[] => {
    if (!text) return [];

    // Strip markdown
    text = text
      .replace(/\*+/g, " ")
      .replace(/#+/g, " ")
      .replace(/_+/g, " ")
      .replace(/`+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    const chunks: string[] = [];
    let current = "";

    for (const sent of sentences) {
      const trimmed = sent.trim();
      if (!trimmed) continue;
      const combinedWords = (current + " " + trimmed).split(/\s+/).length;
      if (current && combinedWords > TARGET_WORDS_PER_CHUNK) {
        chunks.push(current.trim());
        current = trimmed;
      } else {
        current = current ? current + " " + trimmed : trimmed;
      }
    }
    if (current.trim()) chunks.push(current.trim());
    return chunks;
  };

  // ──────────────────────────────────────────────────────────────────
  // FETCH ONE CHUNK (/speak — returns URL)
  // ──────────────────────────────────────────────────────────────────
  const fetchTTS = async (
    text: string,
    id: string,
    signal: AbortSignal,
  ): Promise<string | null> => {
    try {
      const url = `${process.env.REACT_APP_TEXT_TO_SPEECH_SERVICE}/speak?text=${encodeURIComponent(text)}`;
      const res = await fetch(url, { method: "POST", signal });
      const data = await res.json();
      if (!data?.audio_path) throw new Error("No audio_path returned");
      audioMapRef.current[id] = data.audio_path;
      return data.audio_path;
    } catch (err: any) {
      if (err.name === "AbortError") return null;
      console.error("TTS failed", err);
      return null;
    }
  };

  // ──────────────────────────────────────────────────────────────────
  // CONCURRENCY-LIMITED FETCH
  // ──────────────────────────────────────────────────────────────────
  const fetchWithConcurrency = async (
    chunks: string[],
    ids: string[],
    audioUrls: (string | null)[],
    myGeneration: number,
    signal: AbortSignal,
    limit: number = CONCURRENCY,
  ) => {
    let nextIndex = 0;
    const worker = async () => {
      while (true) {
        if (myGeneration !== generationRef.current) return;
        const i = nextIndex++;
        if (i >= chunks.length) return;
        const url = await fetchTTS(chunks[i], ids[i], signal);
        if (myGeneration !== generationRef.current) return;
        audioUrls[i] = url;
      }
    };
    const workers = Array.from(
      { length: Math.min(limit, chunks.length) },
      worker
    );
    await Promise.all(workers);
  };

  // ──────────────────────────────────────────────────────────────────
  // PLAY SINGLE FILE (/speak path)
  // ──────────────────────────────────────────────────────────────────
  const playAudio = (url: string, myGeneration: number) => {
    return new Promise<void>((resolve) => {
      if (myGeneration !== generationRef.current) return resolve();

      const audio = new Audio(url);
      audio.muted = isMuted;
      audio.preload = "auto";
      audioRef.current = audio;
      audioInstancesRef.current.push(audio);

      audio.oncanplaythrough = () => {
        if (myGeneration !== generationRef.current) {
          audio.pause();
          return resolve();
        }
        audio.play().catch((err) => {
          console.error("Play failed:", err);
          resolve();
        });
      };
      audio.onended = () => resolve();
      audio.onerror = (e) => {
        console.error("Audio error:", e);
        resolve();
      };
      audio.load();
    });
  };

  // ──────────────────────────────────────────────────────────────────
  // SEQUENTIAL PLAY WITH CANCELLATION
  // ──────────────────────────────────────────────────────────────────
  const playQueueWithStreaming = async (
    audioUrls: (string | null)[],
    totalChunks: number,
    myGeneration: number,
  ) => {
    setIsPlaying(true);

    for (let i = 0; i < totalChunks; i++) {
      while (audioUrls[i] === undefined) {
        if (myGeneration !== generationRef.current) {
          setIsPlaying(false);
          return;
        }
        await new Promise((r) => setTimeout(r, 50));
      }
      if (myGeneration !== generationRef.current) {
        setIsPlaying(false);
        return;
      }
      if (audioUrls[i] === null) continue;
      setCurrentIndex(i);
      await playAudio(audioUrls[i]!, myGeneration);
      if (myGeneration !== generationRef.current) {
        setIsPlaying(false);
        return;
      }
    }
    setIsPlaying(false);
  };

  // ──────────────────────────────────────────────────────────────────
  // STREAMING PATH (/speak_stream_new)
  // ──────────────────────────────────────────────────────────────────
  const playStreaming = async (text: string, myGeneration: number) => {
    setIsPlaying(true);
    try {
      const url = `${process.env.REACT_APP_TEXT_TO_SPEECH_SERVICE}/speak_stream_new?text=${encodeURIComponent(text)}`;
      const res = await fetch(url, {
        method: "POST",
        signal: abortControllerRef.current?.signal,
      });
      if (!res.body) throw new Error("No stream body");

      // MediaSource setup
      const mediaSource = new MediaSource();
      const audio = new Audio();
      audio.src = URL.createObjectURL(mediaSource);
      audio.muted = isMuted;
      audioRef.current = audio;
      audioInstancesRef.current.push(audio);

      await new Promise<void>((resolve) => {
        mediaSource.addEventListener("sourceopen", () => resolve(), { once: true });
      });

      const sourceBuffer = mediaSource.addSourceBuffer("audio/wav");
      const reader = res.body.getReader();
      audio.play().catch((e) => console.error("Stream play failed:", e));

      const pump = async () => {
        while (true) {
          if (myGeneration !== generationRef.current) {
            reader.cancel();
            break;
          }
          const { done, value } = await reader.read();
          if (done) {
            if (mediaSource.readyState === "open") mediaSource.endOfStream();
            break;
          }
          await new Promise<void>((res) => {
            if (!sourceBuffer.updating) {
              sourceBuffer.appendBuffer(value);
              sourceBuffer.addEventListener("updateend", () => res(), { once: true });
            } else {
              sourceBuffer.addEventListener("updateend", () => {
                sourceBuffer.appendBuffer(value);
                sourceBuffer.addEventListener("updateend", () => res(), { once: true });
              }, { once: true });
            }
          });
        }
      };
      await pump();

      await new Promise<void>((resolve) => {
        audio.onended = () => resolve();
      });
    } catch (err) {
      console.error("Streaming error:", err);
    } finally {
      setIsPlaying(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────
  // HARD STOP
  // ──────────────────────────────────────────────────────────────────
  const stopAll = () => {
    generationRef.current++;
    abortControllerRef.current?.abort();

    audioInstancesRef.current.forEach((a) => {
      try {
        a.pause();
        a.currentTime = 0;
        a.onended = null;
        a.oncanplaythrough = null;
        a.onerror = null;
        a.src = "";
      } catch {}
    });
    audioInstancesRef.current = [];

    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
    }

    setIsPlaying(false);
    setCurrentIndex(0);
  };

  const stop = () => stopAll();

  // ──────────────────────────────────────────────────────────────────
  // PUBLIC: PLAY TEXT
  // ──────────────────────────────────────────────────────────────────
  const playText = async (text: string) => {
    stopAll();
    abortControllerRef.current = new AbortController();
    const myGeneration = generationRef.current;

    if (USE_STREAMING) {
      // Streaming path — single fetch, MediaSource consumes incrementally
      await playStreaming(text, myGeneration);
      return;
    }

    // Standard /speak path — chunk + parallel fetch + sequential play
    const chunks = splitText(text);
    if (chunks.length === 0) return;
    const ids = chunks.map((_, i) => `chunk-${i}`);
    setQueue(ids);
    audioMapRef.current = {};

    const audioUrls: (string | null)[] = new Array(chunks.length);

    fetchWithConcurrency(
      chunks, ids, audioUrls, myGeneration,
      abortControllerRef.current.signal,
      CONCURRENCY,
    );

    await playQueueWithStreaming(audioUrls, chunks.length, myGeneration);
  };

  const pause = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      setIsPlaying(false);
    }
  };

  const resume = async (_text: string) => {
    if (audioRef.current) {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (err) {
        console.error("Resume failed:", err);
      }
    }
  };

  const seek = async (text: string, _index: number) => {
    stopAll();
    await playText(text);
  };

  return {
    playText,
    pause,
    resume,
    stop,
    stopAll,
    seek,
    isPlaying,
    currentIndex,
    audioRef,
    isMuted,
    setIsMuted,
  };
};


// import { useRef, useState, useEffect  } from "react";

// type AudioMap = Record<string, string>;

// export const useTTSQueue = () => {
//   const [isMuted, setIsMuted] = useState(false);
//   const audioMapRef = useRef<AudioMap>({});
//   const [queue, setQueue] = useState<string[]>([]);
//   const [currentIndex, setCurrentIndex] = useState(0);
//   const [isPlaying, setIsPlaying] = useState(false);

//   const audioRef = useRef<HTMLAudioElement | null>(null);
//   const isPausedRef = useRef(false);
//   const audioInstancesRef = useRef<HTMLAudioElement[]>([]);

//   // 🔹 CONFIG
//   const CHUNK_SIZE = 15;
//   const CONCURRENCY = 3;

//   // 🔹 WORD-BASED SPLIT
//   const splitText = (text: string): string[] => {
//     const words = text.split(/\s+/);
//     const chunks: string[] = [];

//     for (let i = 0; i < words.length; i += CHUNK_SIZE) {
//       const chunk = words.slice(i, i + CHUNK_SIZE).join(" ");
//       if (chunk.trim()) chunks.push(chunk);
//     }


//     return chunks ;
//   };

//   const isPlayingStartedRef = useRef(false);

//   useEffect(() => {
//     if (audioRef.current) {
//         audioRef.current.muted = isMuted;
//     }
//   }, [isMuted]);

//   // 🔹 FETCH TTS
//   const fetchTTS = async (text: string, id: string) => {
//     try {
//         // if (audioMapRef.current[id]) {
//         // return audioMapRef.current[id];
//         // }

//         const url = `${process.env.REACT_APP_TEXT_TO_SPEECH_SERVICE}/speak?text=${encodeURIComponent(text)}`;

//         const res = await fetch(url, { method: "POST" });
//         const data = await res.json();

//         if (!data?.audio_path) {
//         throw new Error("No audio_path returned");
//         }

//         const audioUrl = data.audio_path;

//         // ✅ update ref immediately (no async issue)
//         audioMapRef.current[id] = audioUrl;

//         return audioUrl;
//     } catch (err) {
//         console.error("TTS failed", err);
//         return null;
//     }
// };

//   // 🔹 PARALLEL FETCH (BATCHED)
//   const fetchInBatches = async (chunks: string[], ids: string[]) => {
//     const results: (string | null)[] = [];

//     for (let i = 0; i < chunks.length; i += CONCURRENCY) {
//       const batch = chunks.slice(i, i + CONCURRENCY);

//       const batchResults = await Promise.all(
//         batch.map((chunk, idx) => fetchTTS(chunk, ids[i + idx]))
//       );

//       results.push(...batchResults);
//     }

//     return results;
//   };

//   // 🔹 PLAY SINGLE AUDIO (FIXED)
//   const playAudio = (url: string) => {
//     return new Promise<void>((resolve) => {
//       const audio = new Audio(url);
//       audioRef.current = audio;
//       audioInstancesRef.current.push(audio);
//       audio.preload = "auto";

//       // ✅ Apply mute immediately
//       if ((window as any).isMutedGlobal) {
//         audio.muted = isMuted;
//       }

//       audio.oncanplaythrough = () => {
//         audio.play().catch((err) => {
//           console.error("Play failed:", err);
//           resolve();
//         });
//       };

//       audio.onended = () => resolve();

//       audio.onerror = (e) => {
//         console.error("Audio error:", e);
//         resolve();
//       };

//       audio.load();
//     });
//   };

//   const stopAll = () => {
//     // Hard-stop every Audio instance this hook has ever created.
//     audioInstancesRef.current.forEach((a) => {
//       try {
//         a.pause();
//         a.currentTime = 0;
//         a.onended = null;
//         a.oncanplaythrough = null;
//         a.onerror = null;
//         a.src = "";   // detach buffered data; without this Chrome can still emit a chunk
//       } catch {}
//     });
//     audioInstancesRef.current = [];

//     if (audioRef.current) {
//       audioRef.current.pause();
//       audioRef.current = null;
//     }

//     // Break any in-flight playQueueWithStreaming / playQueueWithUrls loops
//     isPausedRef.current = true;

//     setIsPlaying(false);
//     setCurrentIndex(0);
//   };

//   // 🔹 SEQUENTIAL PLAY
//   const playQueueWithUrls = async (
//     audioUrls: (string | null)[],
//     ids: string[]
//   ) => {
//     setIsPlaying(true);

//     for (let i = 0; i < audioUrls.length; i++) {
//       if (isPausedRef.current) break;

//       const url = audioUrls[i];
//       if (!url) continue;

//       console.log("Playing index:", i, url);

//       setCurrentIndex(i);

//       await playAudio(url);
//     }

//     setIsPlaying(false);
//   };

//   // 🔹 PUBLIC: PLAY TEXT
//   const playText = async (text: string) => {
//   const chunks = splitText(text);
//   const ids = chunks.map((_, i) => `chunk-${i}`);
//   stop();
//   audioMapRef.current = {};
//   setQueue([]);
//   setQueue(ids);
//   isPausedRef.current = false;
//   const audioUrls: (string | null)[] = [];

//   let isPlayingStarted = false;


//   for (let i = 0; i < chunks.length; i++) {
//     //  fetch each chunk (parallel-like but progressive)
//     fetchTTS(chunks[i], ids[i]).then((url) => {
//       audioUrls[i] = url;

//       //  start playback as soon as first chunk is ready
//       if (!isPlayingStarted && url) {
//         isPlayingStarted = true;
//         playQueueWithStreaming(audioUrls, ids);
//       }
//     });
//   }
// };


// const playQueueWithStreaming = async (
//   audioUrls: (string | null)[],
//   ids: string[]
// ) => {
//   setIsPlaying(true);

//   for (let i = 0; i < ids.length; i++) {
//     // 🔥 wait until chunk is ready
//     while (!audioUrls[i]) {
//       await new Promise((res) => setTimeout(res, 50));
//     }

//     if (isPausedRef.current) break;

//     setCurrentIndex(i);

//     await playAudio(audioUrls[i]!);
//   }

//   setIsPlaying(false);
// };

//   // 🔹 PAUSE
//   const pause = () => {
//     if (audioRef.current) {
//       audioRef.current.pause();
//       isPausedRef.current = true;
//       setIsPlaying(false);
//     }
//   };

//   // 🔹 RESUME
//   const resume = async (text: string) => {
//     if (!isPausedRef.current) return;

//     isPausedRef.current = false;

//     const chunks = splitText(text);
//     const ids = chunks.map((_, i) => `chunk-${i}`);

//     const audioUrls = await fetchInBatches(chunks, ids);

//     await playQueueWithUrls(audioUrls.slice(currentIndex), ids);
//   };

//   // 🔹 STOP
//   const stop = () => {
//     if (audioRef.current) {
//       audioRef.current.pause();
//       audioRef.current = null;
//     }

//     isPausedRef.current = true;
//     setIsPlaying(false);
//     setCurrentIndex(0);
//   };

//   // 🔹 SEEK
//   const seek = async (text: string, index: number) => {
//     stop();

//     const chunks = splitText(text);
//     const ids = chunks.map((_, i) => `chunk-${i}`);

//     const audioUrls = await fetchInBatches(chunks, ids);

//     await playQueueWithUrls(audioUrls.slice(index), ids);
//   };

//   return {
//     playText,
//     pause,
//     resume,
//     stop,
//     stopAll,
//     seek,
//     isPlaying,
//     currentIndex,
//     audioRef, //  important for mute
//     isMuted,       //  add
//     setIsMuted,
//   };
// };